import React, { useState } from 'react';
import { siteConfig } from '../../config/siteConfig';
import { getFullApiUrl } from '../../config/apiConfig';
import './NewsletterModal.css';

const NewsletterModal = ({ isOpen, onClose }) => {
  const { newsletterModal } = siteConfig;
  
  // Initialize newsletters state from config
  const initializeNewsletters = () => {
    const newsletters = {};
    newsletterModal.newsletters.forEach(newsletter => {
      newsletters[newsletter.id] = newsletter.defaultChecked;
    });
    return newsletters;
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    newsletters: initializeNewsletters()
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      newsletters: {
        ...prev.newsletters,
        [name]: checked
      }
    }));
    // Clear newsletter error when user selects a checkbox
    if (errors.newsletters) {
      setErrors(prev => ({
        ...prev,
        newsletters: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const { validation } = newsletterModal;

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = validation.messages.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = validation.messages.emailInvalid;
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = validation.messages.nameRequired;
    }

    // Newsletter validation - at least one must be selected
    const hasSelectedNewsletter = Object.values(formData.newsletters).some(checked => checked);
    if (!hasSelectedNewsletter) {
      newErrors.newsletters = validation.messages.newsletterRequired;
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});
    setSubmitMessage(null);
    setIsLoading(true);

    // Prepare submission data with selected campaigns
    const selectedCampaigns = [];
    
    // Add selected festival campaigns
    Object.entries(formData.newsletters).forEach(([newsletterId, isSelected]) => {
      if (isSelected) {
        const newsletter = newsletterModal.newsletters.find(n => n.id === newsletterId);
        if (newsletter) {
          selectedCampaigns.push({
            id: newsletter.id,
            campaignId: newsletter.campaignId,
            label: newsletter.label
          });
        }
      }
    });

    // Always add hidden filmservice campaign
    newsletterModal.hiddenCampaigns.forEach(hiddenCampaign => {
      selectedCampaigns.push({
        id: hiddenCampaign.id,
        campaignId: hiddenCampaign.campaignId,
        hidden: true
      });
    });

    const submissionData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      campaigns: selectedCampaigns
    };

    try {
      const apiUrl = getFullApiUrl('/api/newsletter/subscribe');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();
      
      if (response.ok) {
        // Success - show success message
        setSubmitMessage({
          type: 'success',
          message: 'Thank you for subscribing to the FilmService International Newsletter'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          newsletters: initializeNewsletters()
        });
        
      } else if (response.status === 207) {
        // Partial success
        setSubmitMessage({
          type: 'warning',
          message: result.message || 'Some subscriptions may have failed, but you have been subscribed to available newsletters.'
        });
      } else {
        // Error
        setSubmitMessage({
          type: 'error',
          message: result.message || 'Subscription failed. Please try again.'
        });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubmitMessage({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="newsletter-modal-overlay" onClick={onClose}>
      <div className="newsletter-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-button" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        
        <div className="modal-content">
          <h2 className="modal-title">{newsletterModal.title}</h2>
          <p className="modal-subtitle">
            {newsletterModal.subtitle}
          </p>
          
          <form onSubmit={handleSubmit} className="newsletter-form">
            <div className="form-section">
              <div className="input-section">
                
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Name"
                    required
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Your Email"
                    required
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
                
                <button type="submit" className="subscribe-button" disabled={isLoading}>
                  {isLoading ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
              
              <div className="newsletter-options">
                <h3 className="section-title">YES, I WOULD LIKE TO RECEIVE THE LATEST FILMSERVICE INTERNATIONAL NEWS* :</h3>
                
                <div className="checkbox-group">
                  {newsletterModal.newsletters.map((newsletter) => (
                    <label key={newsletter.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        name={newsletter.id}
                        checked={formData.newsletters[newsletter.id]}
                        onChange={handleCheckboxChange}
                      />
                      <span className="checkmark"></span>
                      {newsletter.label}
                    </label>
                  ))}
                </div>
                {errors.newsletters && <span className="error-message newsletter-error">{errors.newsletters}</span>}
              </div>
            </div>
            
            {submitMessage && (
              <div className={`submit-message ${submitMessage.type}`}>
                {submitMessage.message}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewsletterModal; 